import React, { useMemo, useState } from "react";
import loader from "../../../../../../components/recharts/loader.gif";
import RowDetailPopup from "../../Popups/RowDetailPopup";

import { STATE_NAMES } from "./Constants";

interface TableBuySellDeltaProps {
    data?: any; // TopBuyerSellerDeltaResponse
    loading?: boolean;
    viewType?: "drawal" | "injection";
    selectedExchanges?: string[];
    selectedMarkets?: string[];
}

const TableBuySellDelta: React.FC<TableBuySellDeltaProps> = ({
    data,
    loading = false,
    viewType = "drawal",
    selectedExchanges,
    selectedMarkets,
}) => {
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any | null>(null);

    const handleRowClick = (row: any) => {
        if (STATE_NAMES.includes(row.name)) {
            setSelectedRow(row);
            setIsPopupOpen(true);
        }
    };

    const { rows, dateKeys } = useMemo(() => {
        if (!data) return { rows: [], dateKeys: [] };

        const list = viewType === "drawal" ? data.largest_drawal_changes : data.largest_injection_changes;
        if (!list || list.length === 0) return { rows: [], dateKeys: [] };

        // Determine date keys from the first item
        // Keys that are not 'name' or 'delta'
        const firstItem = list[0];
        const keys = Object.keys(firstItem).filter(k =>
            k !== "name" &&
            k !== "delta" &&
            k !== "percent_change"
        );

        // Sort keys to ensure value_range_1 comes before value_range_2
        keys.sort();

        return { rows: list, dateKeys: keys };
    }, [data, viewType]);

    if (loading) {
        return (
            <div className="w-full bg-white flex items-center justify-center min-h-[300px]">
                <img src={loader} width={100} alt="loader" className="block" />
            </div>
        );
    }

    return (
        <>
            <div className="w-full h-full overflow-auto">
                <table className="w-full">
                    <thead className="sticky top-0 z-[1]">
                        <tr>
                            <th className="py-1.55 px-1 text-left text-[11px] font-medium text-[#8C8A96] whitespace-nowrap !bg-[#EEF4FE]">
                                Entity
                            </th>
                            {dateKeys.map((key, idx) => (
                                <th key={key} className="py-1.55 px-1 text-left text-[11px] font-medium text-[#8C8A96] whitespace-nowrap !bg-[#EEF4FE]">
                                    {key === "value_range_1" ? "Day 1 (MW)" : key === "value_range_2" ? "Day 2 (MW)" : key}
                                </th>
                            ))}
                            <th className="py-1.55 px-1 text-left text-[11px] font-medium text-[#8C8A96] whitespace-nowrap !bg-[#EEF4FE]">
                                Change (MW)
                            </th>
                            <th className="py-1.55 px-1 text-left text-[11px] font-medium text-[#8C8A96] whitespace-nowrap !bg-[#EEF4FE]">
                                Change (%)
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {rows.length > 0 ? (
                            rows.map((row: any, index: number) => {
                                const isClickable = STATE_NAMES.includes(row.name);
                                return (
                                    <tr
                                        key={index}
                                        className={`border-b border-[#F0F4F8] hover:bg-[#F8FAFC] transition-colors ${isClickable ? "cursor-pointer" : "cursor-default"}`}
                                        onClick={() => handleRowClick(row)}
                                    >
                                        <td className="py-1.55 px-1 text-left whitespace-nowrap text-[11px] font-medium text-[#34656D] max-w-[80px] truncate" title={row.name}>
                                            {row.name}
                                        </td>
                                        {dateKeys.map((key) => (
                                            <td key={key} className="py-1.55 px-1 text-left whitespace-nowrap text-[11px] text-[#525062]">
                                                {typeof row[key] === 'number' ? row[key].toFixed(2) : row[key]}
                                            </td>
                                        ))}
                                        <td className="py-1.55 px-1 text-left whitespace-nowrap text-[11px] text-[#525062]">
                                            {typeof row.delta === 'number' ? row.delta.toFixed(2) : row.delta}
                                        </td>
                                        <td className="py-1.55 px-1 text-left whitespace-nowrap text-[11px] text-[#525062]">
                                            {typeof row.percent_change === 'number' ? `${row.percent_change.toFixed(2)}%` : row.percent_change}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={3 + dateKeys.length} className="py-8 text-center text-gray-500">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Row Detail Popup */}
            <RowDetailPopup
                isOpen={isPopupOpen}
                onClose={() => setIsPopupOpen(false)}
                rowData={selectedRow}
                selectedExchanges={selectedExchanges}
                selectedMarkets={selectedMarkets}
            />
        </>
    );
};

export default TableBuySellDelta;
